import type { ReactNode } from "react";
import OrderBookTable from "src/Tables/Order/OrderBookTable";
import { IOpenOrder } from "src/Interfaces/marketmaking";
import { OrderBook } from "src/Interfaces/orderInterfaces";
import { addSeperatorToInput } from "src/helper";
import "./MarketMakingOrderBookPanel.css";

export interface MarketMakingOrderBookDetails {
  baseInsPriceLive?: number | null;
  baseInsPriceYesterday?: number | null;
  monthlyGainCoveredCall?: number | null;
  taskStatus?: string | null;
  monthlyGainCoveredCallRef?: number | null;
  correspondingRef?: string | null;
  lastIVChangeByVolumeStr?: string | null;
  lastFactorTriggerAt?: string | null;
  leg1AtmHedger?: string | null;
  leg1Iv?: number | null;
  liveFairPrice?: number | null;
  leg2AtmHedger?: string | null;
  leg2Iv?: number | null;
  vwsp?: number | null;
  fTheor?: number | null;
  closingPrice?: number | null;
  midpoint?: number | null;
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
  priceYesterday?: number | null;
}

interface Props {
  orderBook?: OrderBook;
  openOrders?: IOpenOrder[];
  details?: MarketMakingOrderBookDetails | null;
  mode?: "automatic" | "futures" | "base";
  board?: ReactNode;
}

const MarketMakingOrderBookPanel = ({ orderBook, openOrders, details, mode = "automatic", board }: Props) => {
  const tableInfo = orderBook == null ? orderBook : {
    ...orderBook,
    minPricePermit: orderBook.minPricePermit ?? details?.minPricePermit,
    maxPricePermit: orderBook.maxPricePermit ?? details?.maxPricePermit,
  };
  const getKeyValueJSX = (title: string, value: string | number) => (
    <div className="flex flex-row justify-start items-center gap-3 text-xs">
      <div className="font-bold whitespace-nowrap">{title}:</div>
      <div className={`ltr ${value == 0 ? "text-red-500" : "text-gray-950"}`}>
        {value}
      </div>
    </div>
  );

  const formatPrice = (value?: number | null) =>
    value ? addSeperatorToInput(value.toString(), "price") : "-";
  const formatFuturesPrice = (value?: number | null) =>
    value == null ? "-" : value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  const formatFuturesPriceRange = (min?: number | null, max?: number | null) =>
    min != null && max != null && min > 0 && max >= min
      ? `${formatFuturesPrice(max)} - ${formatFuturesPrice(min)}` : "-";
  const minPermit = details?.minPricePermit;
  const maxPermit = details?.maxPricePermit;
  const yesterdayClose = details?.priceYesterday != null &&
    Number.isFinite(details.priceYesterday) && details.priceYesterday > 0
    ? details.priceYesterday
    : minPermit != null && maxPermit != null &&
      Number.isFinite(minPermit) && Number.isFinite(maxPermit) &&
      minPermit > 0 && maxPermit >= minPermit
      ? (minPermit + maxPermit) / 2 : null;
  const getFuturesPriceJSX = (title: string, value?: number | null,
    baseline: number | null | undefined = yesterdayClose) => {
    const close = baseline != null && Number.isFinite(baseline) && baseline > 0
      ? baseline : null;
    const change = value != null && Number.isFinite(value) && value > 0 &&
      close != null ? value - close : null;
    const changePercent = change != null && close != null
      ? change / close * 100 : null;
    const sign = changePercent != null && changePercent > 0 ? "+" : changePercent != null && changePercent < 0 ? "−" : "";
    const color = change == null || change === 0 ? "text-slate-500" :
      change > 0 ? "text-green-600" : "text-red-500";

    return <div className="flex flex-row flex-wrap items-center gap-x-2 text-xs">
      <span className="font-bold whitespace-nowrap">{title}:</span>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" dir="rtl">
        <span className="text-gray-950" dir="ltr">{formatFuturesPrice(value)}</span>
        {changePercent != null && <span className={`tabular-nums ${color}`} dir="ltr">
          ({sign}{Math.abs(changePercent).toFixed(2)}%)
        </span>}
      </span>
    </div>;
  };

  return (
    <div className={board
      ? "market-making-order-book-panel market-making-order-book-panel--board mx-auto p-3 h-auto w-full"
      : "flex justify-start items-center mx-auto gap-3 p-3 h-auto w-full"}>
      <div className="market-making-order-book-panel__book overflow-x-auto p-2">
        <OrderBookTable tableInfo={tableInfo} openOrders={openOrders} />
      </div>
      <div className="market-making-order-book-panel__details flex flex-col gap-y-6">
        {mode === "futures" || mode === "base" ? <div className="market-making-order-book-panel__futures-details grid gap-x-5 gap-y-2 self-start">
          {getFuturesPriceJSX("قیمت لحظه ای پایه", details?.baseInsPriceLive,
            details?.baseInsPriceYesterday ?? null)}
          {getKeyValueJSX("زمان تغییر براساس حجم", details?.lastFactorTriggerAt ?? "")}
          {getFuturesPriceJSX("قیمت تعادلی لحظه ای", details?.liveFairPrice)}
          {getKeyValueJSX("قیمت مجاز", formatFuturesPriceRange(details?.minPricePermit, details?.maxPricePermit))}
          {getFuturesPriceJSX(mode === "base" ? "قیمت پایانی" : "VWSP",
            mode === "base" ? details?.closingPrice : details?.vwsp)}
          {getFuturesPriceJSX(mode === "base" ? "میانگین سرخط ۱۰ ثانیه اخیر" : "F-Theor",
            mode === "base" ? details?.midpoint : details?.fTheor)}
        </div> : <>
        <div className="grid grid-cols-2 gap-x-5 gap-y-2 self-start">
          {getKeyValueJSX("قیمت لحظه ای نماد پایه", formatPrice(details?.baseInsPriceLive))}
          {getKeyValueJSX("بازده ماهانه کاوردکال", details?.monthlyGainCoveredCall ?? "-")}
          {getKeyValueJSX("وضعیت", details?.taskStatus ?? "-")}
          {getKeyValueJSX("بازده ماهانه کاوردکال معیار", details?.monthlyGainCoveredCallRef ?? "-")}
          {getKeyValueJSX("معیار متناظر", details?.correspondingRef ?? "-")}
        </div>
        <div className="grid grid-cols-3 gap-x-5 gap-y-2 self-start">
          {getKeyValueJSX("زمان تغییر براساس حجم", details?.lastIVChangeByVolumeStr ?? "-")}
          {getKeyValueJSX("لگ اول ATM هجر", details?.leg1AtmHedger ?? "-")}
          {getKeyValueJSX("IV لگ اول", details?.leg1Iv ?? "-")}
          {getKeyValueJSX("قیمت تعادلی لحظه ای", formatPrice(details?.liveFairPrice))}
          {getKeyValueJSX("لگ دوم ATM هجر", details?.leg2AtmHedger ?? "-")}
          {getKeyValueJSX("IV لگ دوم", details?.leg2Iv ?? "-")}
        </div>
        </>}
      </div>
      {board && <div className="market-making-order-book-panel__board">{board}</div>}
    </div>
  );
};

export default MarketMakingOrderBookPanel;
